import { takeEvery, put, call, all, Effect, takeLatest } from "redux-saga/effects";

import { userApi as UserApi } from "../../api/UserApi";
import { filterNulls, maybeCleanInt } from "../../utils/dataConverter";
import { userSlice } from "../reducer/user";
import { userSetupSlice, UserSetupStep } from "../reducer/user_setup";
import { push } from "redux-first-history";
import { User } from "../../model/user";
import { PayloadAction } from "@reduxjs/toolkit";
import { spouseApi as SpouseApi } from "../../api/SpouseApi";
import { SpouseInfo, toSpouseModel } from "../../model/spouse_info.d";
import { SpouseResponse, UserResponse } from "../../model/response";
import { AxiosResponse } from "axios";
import { logout } from "../actions/auth";
import { ExitType } from "../../constants/common";

function* fetchUserSaga(): Generator<Effect, void, any> {
  try {
    const response = (yield call(UserApi.getUser)) as AxiosResponse<UserResponse>;
    let sresponse;
    try {
      sresponse = (yield call(SpouseApi.getSpouse)) as AxiosResponse<SpouseResponse>;
    } catch (error: any) {
      sresponse = { data: { spouse: {} } };
    }
    if (sresponse.data.spouse.id) {
      yield put(userSlice.actions.fetchUserSuccess({
        ...response.data.user,
        spouse_first_name: sresponse.data.spouse.first_name,
        spouse_last_name: sresponse.data.spouse.last_name,
        spouse_social_security: sresponse.data.spouse.social_security
      }));
      return;
    }
    yield put(userSlice.actions.fetchUserSuccess(response.data.user));
  } catch (error: any) {
    console.log(error);
    yield put(userSlice.actions.fetchUserError(error.message));
    yield put(logout());
  }
}

/// Update user data
function* updateUserSaga({ payload }: PayloadAction<User>): Generator<Effect, void, any> {
  try {
    yield put(userSlice.actions.resetUserError());
    const user: User = filterNulls({
      ...payload,
      phone_number: maybeCleanInt(payload.phone_number),
    });

    delete user.social_security;
    const response = yield call(UserApi.updateUser, user);
    yield put(userSlice.actions.updateUserSuccess(response.data.user));
    yield put(userSetupSlice.actions.setDisableNext(false));
  } catch (error: any) {
    yield put(userSlice.actions.updateUserError(error.message));
  }
}

/// Update user data
function* updateUserCompleteSaga({ payload }: PayloadAction<User>): Generator<Effect, void, any> {
  try {
    yield put(userSlice.actions.clearUserError());
    const user: User = filterNulls({
      ...payload,
      phone_number: maybeCleanInt(payload.phone_number),
    });

    delete user.social_security;
    const response = yield call(UserApi.updateUser, user);
    yield put(userSlice.actions.updateUserSuccess(response.data.user));
    yield put(userSetupSlice.actions.setDisableNext(false));
    if (payload.is_jointfiling) {
      yield put(userSlice.actions.updateSpouse(payload));
    } else {
      yield put(userSlice.actions.deleteSpouse());
    }
    yield put(push("/user/review"));
  } catch (error: any) {
    yield put(userSetupSlice.actions.setExitMessageType(ExitType.UserData));
    yield put(userSlice.actions.updateUserError(error.message));
    yield put(push("/user/sorry"));
  } finally {
    yield put(userSlice.actions.doneLoading());
  }
}

function* updateSpouseSaga({ payload }: PayloadAction<SpouseInfo>): Generator<Effect, void, any> {
  const spouse = toSpouseModel(payload);
  try {
    try {
      const response = (yield call(SpouseApi.getSpouse)) as AxiosResponse<SpouseResponse>;
      if (response.data.spouse.id) {
        delete spouse.social_security;
        yield call(SpouseApi.updateSpouse, spouse);
      } else {
        yield call(SpouseApi.createSpouse, spouse);
      }
      yield put(userSlice.actions.updateUserData({
        spouse_social_security: response.data.spouse.social_security
      }));
    } catch (error: any) {
      console.log(error);
      yield call(SpouseApi.createSpouse, spouse);
    }
  } catch (error: any) {
    const ssErrors = error?.errors?.social_security;
    yield put(userSlice.actions.updateUserError(!ssErrors ? "Failed to save spouse" : "Failed to save spouse: " + ssErrors[0]));
  }

  yield put(push("/user/review"));
  yield put(userSlice.actions.doneLoading());
}

function* deleteSpouseSaga(): Generator<Effect, void, any> {
  try {
    const response = yield call(SpouseApi.getSpouse);
    if (response.data.spouse) {
      yield call(SpouseApi.deleteSpouse);
    }
  } catch (error: any) {
  } finally {
    yield put(userSlice.actions.deleteSpouseComplete());
  }
}

export default function* userSaga() {
  yield all([takeEvery(userSlice.actions.fetchUser.type, fetchUserSaga)]);
  yield all([takeEvery(userSlice.actions.updateUser.type, updateUserSaga)]);
  yield all([takeEvery(userSlice.actions.updateUserComplete.type, updateUserCompleteSaga)]);
  yield all([takeEvery(userSlice.actions.updateSpouse.type, updateSpouseSaga)]);
  yield all([takeEvery(userSlice.actions.deleteSpouse.type, deleteSpouseSaga)]);
}
